import { AfterViewInit, Component, Host, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { app } from '@df/core/app';
import { UiComponent } from '@df/ui/ui.component';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { CategoryViewComponent } from '../category-view.component';

export namespace NCategoryViewLayout {
    export const BLOCK_ID = 'category-page-layout';
    export interface IBlockData extends Catalog.IBlockData<IContent> {}

    export interface IContent {
        /**
         * Top components list
         */
        topComponents: NICmsContentComponent.IData[];

        /**
         * Middle components list
         */
        middleComponents: NICmsContentComponent.IData[];

        /**
         * Bottom components list
         */
        bottomComponents: NICmsContentComponent.IData[];
        quickFilters: IQuickFilter[];
    }

    export interface IQuickFilter {
        label: string;
        filters: IQuickFilterGroup[];
    }

    export interface IQuickFilterGroup {
        attrCode: string;
        value: (number | string)[];
    }
}
/**

/**
 * Component definition
 */
@Component({
    selector: 'category-view-layout',
    templateUrl: './category-view-layout.html'
})
@ICmsComponent<CategoryViewLayoutComponent>({
    id: NCategoryViewLayout.BLOCK_ID,
    block: {
        url: NCategoryViewLayout.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Category page layout',
    description: 'Category page layout block',
    fields: [
        <NICmsContentField.IComponentsField>{
            key: 'topComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Top components, above name',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IComponentsField>{
            key: 'middleComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Middle components, below name',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IRepeaterField>{
            key: 'quickFilters',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Quick filters',
            itemName: 'filter',
            itemNameField: 'label',
            canShow: data =>
                app?.meta?.model?.type === 'category' &&
                !!(app.meta.model.data as ICategoryDataV3)?.filters?.filter(
                    filter => filter.type === 'attribute' && app.resources?.attribute?.getExisting(filter.source || '')?.data?.options
                ).length,
            fields: [
                {
                    key: 'label',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Label',
                    default: 'Quick filter',
                    required: true
                },
                {
                    key: 'filters',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Filters',
                    itemName: 'Filter',
                    itemNameField: 'attrCode',
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'attrCode',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Attribute',
                            options: data =>
                                (app?.meta?.model?.data as ICategoryDataV3).filters
                                    ?.filter(
                                        filter =>
                                            filter.type === 'attribute' &&
                                            app.resources?.attribute?.getExisting(filter.source || '')?.data?.options
                                    )
                                    .map(filter => ({
                                        key: filter.source,
                                        title: filter.source
                                    })) || [],
                            description: 'If same attribute is selected several times, last value will be respected.',
                            onChange: (event: NICmsContentField.IFieldEvent) => {
                                if (
                                    event.scopeData.value &&
                                    !app.resources?.attribute
                                        ?.getExisting(event.value)
                                        ?.data?.options?.find(option => option.value === event.scopeData.value)
                                ) {
                                    return {
                                        changes: [
                                            {
                                                changeType: NICmsDataChange.EChangeType.delete,
                                                scopeData: event.scopeData,
                                                key: 'value'
                                            }
                                        ]
                                    };
                                }
                                return;
                            },
                            required: true
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'value',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Value',
                            options: data =>
                                (app.resources?.attribute?.getExisting(data.attrCode)?.data?.options || []).map(option => ({
                                    key: option.value,
                                    title: option.label
                                })),
                            multiple: true,
                            canShow: data => !!data.attrCode,
                            required: true
                        }
                    ]
                }
            ]
        }
    ]
})
export class CategoryViewLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy, AfterViewInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NCategoryViewLayout.IContent;

    @Input()
    variant?: string;

    protected override canDetach = false;

    /**
     * Dependencies
     */
    constructor(
        private afterContentInit: AfterContentInitDirectiveService,
        @Host() public categoryView: CategoryViewComponent
    ) {
        super();
    }

    /**
     * Category shorthand
     */
    get category() {
        return this.categoryView.model;
    }

    override ngOnInit() {
        this.afterContentInit.lock(this);
        super.ngOnInit();
    }

    ngAfterViewInit(): void {
        this.timeoutService.setTimeout(() => {
            this.afterContentInit.unlock(this);
        }, 1000);
    }

    /////////////////// HELPERS ////////////////////

    override detectChanges() {
        if (this.initiated) {
            this.client.isCms ? this.categoryView.detectChanges() : super.detectChanges();
        }
    }
}
